import {
  Delete,
  Edit,
  MoreHoriz,
  SaveAlt,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { Card, CardContent, Link, styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import { OnboardingCustomRequestType } from 'further-types/lp/onboarding-custom-request';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column, Row } from 'further-ui/components/Layout';
import RichText from 'further-ui/components/RichText';
import Menu from 'further-ui/components/Menu';
import { useDisclosure } from 'further-ui/hooks';
import DraggableList from 'components/DraggableList';

const Content = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const DragIndicator = styled(DragIndicatorIcon)({
  display: 'flex',
  cursor: 'move',
});

const DocumentLink = styled(Link)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.secondary.main,
  textDecoration: 'none',
}));

const getRequestTypeLabel = (
  type: OnboardingCustomRequestType,
  isRequired: boolean,
) => {
  const typeText =
    type === OnboardingCustomRequestType.Text
      ? 'text'
      : type === OnboardingCustomRequestType.File
      ? 'file upload'
      : 'file and text';

  return ` ${isRequired ? 'Required' : 'Optional'} ${typeText} request`;
};

type Props = {
  id: string;
  title: string;
  description: string;
  type: OnboardingCustomRequestType;
  documents: Array<{
    name: string;
    path: string;
    previewUrl: string;
  }>;
  isRequired: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  isLoading: boolean;
};

const Request: React.FC<Props> = ({
  id,
  title,
  description,
  type,
  documents,
  isRequired,
  onEdit,
  onDelete,
  isLoading,
}) => {
  const deleteDisclosure = useDisclosure();

  const handleClickEditRequest = () => {
    onEdit?.(id);
  };

  return (
    <Card variant="outlined">
      <Content>
        <Column spacing="sm" fullWidth>
          <Row fullWidth centered justify="space-between">
            <Typography variant="h6">{title}</Typography>
            <Row spacing="xs" centered>
              {onEdit ?? onDelete ? (
                <Menu
                  handleSize="small"
                  handleIcon={MoreHoriz}
                  items={[
                    {
                      label: 'Edit request',
                      icon: Edit,
                      hidden: !onEdit,
                      onClick: handleClickEditRequest,
                      disabled: isLoading,
                    },
                    {
                      label: 'Delete request',
                      icon: Delete,
                      hidden: !onDelete,
                      onClick: deleteDisclosure.onOpen,
                      disabled: isLoading,
                    },
                  ]}
                />
              ) : null}
              <DraggableList.Handle>
                <DragIndicator fontSize="small" />
              </DraggableList.Handle>
            </Row>
          </Row>
          {description ? <RichText text={description} /> : null}
          {documents.length > 0 ? (
            <Column spacing="xs" fullWidth>
              {documents.map((document) => (
                <Row fullWidth key={document.path} spacing="xs">
                  <SaveAlt fontSize="small" />
                  <DocumentLink
                    href={document.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {document.name}
                  </DocumentLink>
                </Row>
              ))}
            </Column>
          ) : null}
          <Typography variant="body2" fontStyle="italic">
            {getRequestTypeLabel(type, isRequired)}
          </Typography>
        </Column>
        <ConfirmDialog
          open={deleteDisclosure.isOpen}
          onClose={deleteDisclosure.onClose}
          title="Delete request"
          content="Are you sure you want to delete this request? This action cannot be undone."
          actions={{
            confirm: {
              label: 'Delete',
              color: 'error',
              onClick: () => {
                deleteDisclosure.onClose();
                onDelete?.(id);
              },
            },
          }}
        />
      </Content>
    </Card>
  );
};

export default Request;
